// All your imports remain unchanged
import { Component, Inject, Input, OnChanges, OnInit, Optional, SimpleChanges } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { EmailService } from 'src/app/services/email-service';
import { MailTemplateComponent } from '../mail-template/mail-template.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Component({
    selector: 'app-preview-mail',
    templateUrl: './preview-mail.component.html',
    styleUrls: ['./preview-mail.component.scss'],
    standalone: false
})
export class PreviewMailComponent implements OnChanges {
  @Input() pageType: string = '';
  @Input() emailId: string = '';
  public mailId: string;

  emailDetails: any = null;
  isLoading: boolean = true;
  conversationThread: any[] = [];
  attachments: any;
  to: string = '';
  subject: string = '';
  message: string = '';
  uploadedFiles: File[] = [];
  isReplySectionVisible: boolean = false;
  isForwardSectionVisible: boolean = false;
  forwardTo: string = '';
  forwardCc: string = '';
  forwardSubject: string = '';
  forwardBody: string = '';
  forwardUploadedFiles: File[] = [];
  userForwardMessage: string = '';
  messageHtml: SafeHtml = '';


 constructor(
  @Optional() public dialogRef: MatDialogRef<PreviewMailComponent>,
  public emailService: EmailService,
  public dialog: MatDialog,
  public snackBar: MatSnackBar,
  private sanitizer: DomSanitizer,
  @Optional() @Inject(MAT_DIALOG_DATA) public data: any
) {
  this.mailId = data?.mailId ?? '';
  this.pageType = data?.pageType ?? ''; 

  if (this.mailId && this.pageType === 'sidebar') {
    console.log('sidebar');
    this.fetchEmailDetails(this.mailId);
  }
}


  ngOnChanges(changes: SimpleChanges): void {
  const curr = changes['emailId']?.currentValue;
  const prev = changes['emailId']?.previousValue;

  const normalizedCurr = curr?.toString().trim().toLowerCase();
  const normalizedPrev = prev?.toString().trim().toLowerCase();

  if (normalizedCurr !== normalizedPrev && this.pageType === 'fullscreen') {
    this.mailId = curr;
    this.fetchEmailDetails(this.mailId);
  }
}

 fetchEmailDetails(mailId: any): void {
  this.isLoading = true;
  this.emailService.getEmailsbyId(mailId).then((email) => {
    this.emailDetails = email;

    const subject = this.emailDetails?.subject?.toLowerCase() || '';
    const isReplyOrForward = subject.startsWith('re:') || subject.startsWith('fwd:');

    if (this.emailService.isSentTabSelected) {
      this.conversationThread = [this.aggData(this.emailDetails)];
      this.isLoading = false;
    } else if (this.emailDetails?.conversationId && isReplyOrForward) {
      this.fetchConversationThread(this.emailDetails.conversationId);
    } else {
      
      this.conversationThread = [this.aggData(this.emailDetails)];
      this.isLoading = false;
    }

  })
  .catch((error) => {
    console.error('Error fetching email details:', error);
    this.isLoading = false;
  });
}

aggData(filteredMessages: any): any {
  if (Array.isArray(filteredMessages)) {
    return filteredMessages.map((msg: any) => this.processMessageContent(msg));
  } else {
    return this.processMessageContent(filteredMessages);
  }
}

processMessageContent(msg: any): any {
  if (!msg) return msg;
  if (msg.body?.content && msg.attachments?.length) {
    msg.sanitizedBody = this.processInlineImages(msg.body.content, msg.attachments);
  } else {
    msg.sanitizedBody = this.sanitizer.bypassSecurityTrustHtml(msg.body?.content || '');
  }
  return msg;
}



  fetchConversationThread(conversationId: string): void {
  if (!conversationId) return;

  this.emailService.getConversationMessagesWithAttachments(conversationId)
    .then((messages: any[]) => {
      const uniqueMessages = messages.filter((message, index, self) =>
        index === self.findIndex((m) => m.id === message.id)
      );

      const sortedMessages = uniqueMessages.sort(
        (a, b) => new Date(b.receivedDateTime).getTime() - new Date(a.receivedDateTime).getTime()
      );

      let filteredMessages: any[] = [];
      const seenBodies: string[] = [];

      for (const msg of sortedMessages) {
        const content = (msg.body?.content || '').replace(/\s+/g, ' ').trim();
        const isAlreadyQuoted = seenBodies.some(existing =>
          existing.includes(content.substring(0, 100))
        );

        if (!isAlreadyQuoted) {
          filteredMessages.push(msg);
          seenBodies.push(content);
        }
      }

      filteredMessages = filteredMessages.map(msg => {
        if (msg?.body?.content && msg?.attachments?.length) {
          msg.sanitizedBody = this.processInlineImages(msg.body.content, msg.attachments);
        } else {
          msg.sanitizedBody = this.sanitizer.bypassSecurityTrustHtml(msg.body?.content || '');
        }
        return msg;
      });

      this.conversationThread = filteredMessages;
      this.isLoading = false;
    })
    .catch((error) => {
      console.error('Error fetching conversation thread with attachments:', error);
      this.isLoading = false;
    });
}



  popUpClose(): void {
    this.dialogRef.close();
  }

  getFormattedEmailBody(bodyPreview: any): string {
    return bodyPreview?.replace(/\r\n|\r|\n/g, '<br>') ?? '';
  }

  base64ToBlob(base64: string, contentType: string): Blob {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length).fill(0).map((_, i) => byteCharacters.charCodeAt(i));
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: contentType });
  }

  downloadAttachment(attachment: any): void {
    if (attachment.contentBytes) {
      const blob = this.base64ToBlob(attachment.contentBytes, attachment.contentType);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.name;
      a.click();
      window.URL.revokeObjectURL(url);
    } else {
      this.emailService.getAttachmentContent(this.emailDetails.id, attachment.id)
        .then((content: string) => {
          const blob = this.base64ToBlob(content, attachment.contentType);
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = attachment.name;
          a.click();
          window.URL.revokeObjectURL(url);
        })
        .catch((error) => {
          console.error('Error downloading attachment:', error);
        });
    }
  }
toggleReplySection(): void {
  this.isReplySectionVisible = !this.isReplySectionVisible;

  if (this.isReplySectionVisible && this.emailDetails) {
    this.to = this.emailDetails.fromEmail || '';
    this.subject = 'Re: ' + (this.emailDetails.subject || '');

    // 👇 User input should be empty
    this.message = '';

    // 👇 Quoted original message (for display only, not editable)
    const inlineHtml = this.generateInlineImageHtml(
      this.emailDetails.body?.content || '',
      this.emailDetails.attachments || []
    );
    this.messageHtml = this.sanitizer.bypassSecurityTrustHtml(
      `<br><br>--- Original Message ---<br>${inlineHtml}`
    );
  }
}

  formatText(command: string): void {
    document.execCommand(command, false, undefined);
  }

onMessageInput(event: Event): void {
  const target = event.target as HTMLElement;
  this.message = target.innerHTML;
}

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      for (let i = 0; i < input.files.length; i++) {
        this.uploadedFiles.push(input.files[i]);
      }
    }
  }

  removeFile(index: number): void {
    this.uploadedFiles.splice(index, 1);
  }

  sendReply(): void {
    if (!this.to.trim() || !this.subject.trim() || !this.message.trim()) {
      this.snackBar.open('Please fill all fields before sending.', 'Close', { duration: 3000 });
      return;
    }

    const replyPayload = {
      to: this.to,
      subject: this.subject,
      body: this.message,
      attachments: this.uploadedFiles,
    };

    this.emailService.replyToEmail(this.emailDetails.id, replyPayload)
      .then(() => {
        this.snackBar.open('Reply sent successfully.', 'Close', { duration: 3000 });
        this.to = '';
        this.subject = '';
        this.message = '';
        this.uploadedFiles = [];
        this.isReplySectionVisible = false;
      })
      .catch(() => {
        this.snackBar.open('Failed to send reply.', 'Close', { duration: 3000 });
      });
  }

  toggleForwardSection(): void {
    this.isForwardSectionVisible = !this.isForwardSectionVisible;
    this.isReplySectionVisible = false;
    if (this.isForwardSectionVisible) {
      this.forwardTo = '';
      this.forwardCc = '';
      this.forwardSubject = this.emailDetails?.subject ? 'Fwd: ' + this.emailDetails.subject : '';
      this.forwardBody = this.emailDetails?.body?.content || this.emailDetails?.bodyPreview;
      this.forwardUploadedFiles = [];
    }
  }

  onForwardFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      for (let i = 0; i < input.files.length; i++) {
        this.forwardUploadedFiles.push(input.files[i]);
      }
    }
  }

  removeForwardFile(index: number): void {
    this.forwardUploadedFiles.splice(index, 1);
  }

  onForwardBodyInput(event: Event): void {
    const target = event.target as HTMLElement;
    this.forwardBody = target.innerText;
  }

  sendForward(): void {
    if (!this.forwardTo.trim()) {
      this.snackBar.open('Recipient email address (To) cannot be empty.', 'Close', { duration: 3000 });
      return;
    }
    if (!this.forwardSubject.trim()) {
      this.snackBar.open('Subject cannot be empty.', 'Close', { duration: 3000 });
      return;
    }

    const userMessageHtml = this.userForwardMessage ? `<div>${this.userForwardMessage.replace(/\n/g, '<br>')}</div>` : '';
    const fullBody = userMessageHtml;

    const payload = {
      to: this.forwardTo.split(',').map(e => e.trim()).filter(e => e),
      cc: this.forwardCc ? this.forwardCc.split(',').map(e => e.trim()).filter(e => e) : [],
      subject: this.forwardSubject,
      body: fullBody,
      attachments: this.forwardUploadedFiles
    };

    this.emailService.forwardEmail(this.emailDetails.id, payload)
      .then(() => {
        this.snackBar.open('Mail forwarded successfully.', 'Close', { duration: 3000 });
        this.isForwardSectionVisible = false;
        this.userForwardMessage = '';
      })
      .catch(() => {
        this.snackBar.open('Failed to forward mail.', 'Close', { duration: 3000 });
      });
  }

  sanitizeEmailBody(html: string): string {
    let cleaned = html.replace(/class=["']?Mso[a-zA-Z0-9]+["']?/g, '');
    cleaned = cleaned.replace(/style=["'][^"']*["']/g, '');
    cleaned = cleaned.replace(/<p/g, '<div').replace(/<\/p>/g, '</div>');
    return cleaned;
  }

  openMailTemplates(): void {
    this.dialog.open(MailTemplateComponent, {
      height: '70%',
      width: '30%',
      disableClose: false,
      panelClass: "du-custom-modalbox",
      data: 'comment'
    });
  }

  getFileIcon(contentType: string): string {
    if (contentType.startsWith('image/')) return 'image';
    if (contentType === 'application/pdf') return 'picture_as_pdf';
    if (contentType.includes('spreadsheetml') || contentType.includes('excel')) return 'table_chart';
    if (contentType.includes('word')) return 'description';
    if (contentType.startsWith('text/')) return 'text_snippet';
    return 'insert_drive_file';
  }

  trackByMessageId(index: number, message: any): string {
    return message.id;
  }
generateInlineImageHtml(bodyHtml: string, attachments: any[]): string {
  let updatedHtml = bodyHtml;

  attachments?.forEach(attachment => {
    if (
      attachment.isInline &&
      attachment.contentId &&
      attachment.contentBytes &&
      attachment.contentBytes.length > 0
    ) {
      let contentId = attachment.contentId.replace(/[<>]/g, '');
      const contentType = attachment.contentType || 'image/jpeg';
      const base64Data = attachment.contentBytes;
      const dataUrl = `data:${contentType};base64,${base64Data}`;
      const regex = new RegExp(`(unsafe:)?cid:<?${contentId}>?`, 'gi');
      updatedHtml = updatedHtml.replace(regex, dataUrl);
    }
  });

  return updatedHtml;
}




processInlineImages(bodyHtml: string, attachments: any[]): SafeHtml {
  let updatedHtml = bodyHtml;

  attachments?.forEach(attachment => {
    if (
      attachment.isInline &&
      attachment.contentId &&
      attachment.contentBytes &&
      attachment.contentBytes.length > 0
    ) {
      let contentId = attachment.contentId;

      // Remove angle brackets if present
      contentId = contentId.replace(/[<>]/g, '');

      // Fallback for missing content type
      const contentType = attachment.contentType || 'image/jpeg';
      const base64Data = attachment.contentBytes;

      // Construct the data URL
      const dataUrl = `data:${contentType};base64,${base64Data}`;

      // Match variations: cid:, <cid:>, unsafe:cid:
      const regex = new RegExp(`(unsafe:)?cid:(<)?${contentId}(>)?`, 'gi');

      // Log for debugging
      console.log(`Replacing cid:${contentId} with data URL.`);

      updatedHtml = updatedHtml.replace(regex, dataUrl);
    }
  });

  return this.sanitizer.bypassSecurityTrustHtml(updatedHtml);
}


}
